/**
 * Lift2Fit — the rating block beside Help (migration 0037).
 *
 * When the help thread is open, the panel on the other side of the conversation
 * is about LIFT2FIT, not about a client: a client's programme summary is noise
 * while someone is asking the platform for help. So the side panel asks the
 * account to rate the platform and leaves a note — one review per account,
 * editable, read by the founder on the platform side of the same thread.
 *
 * The same component is used on the chat workspace, on the standalone
 * `#/help` page and (read-only) on the admin's side of a thread.
 */
import { useEffect, useState } from 'react';
import { api, type PlatformReview } from '@/lib/api';
import { Button } from '@/components/ui';
import { cn } from '@/lib/format';
import { LiveMessage } from '../components';
import { useSession } from '@/lib/useSession';

/** A five-star row. Filled stars are `accent`; the rest stay hairline. */
export function StarRow({
  rating,
  size = 'sm',
  className,
}: {
  rating: number;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const glyph = size === 'md' ? 'text-[18px]' : 'text-[13px]';
  return (
    <span
      className={cn('inline-flex items-center gap-0.5', glyph, className)}
      role="img"
      aria-label={`${rating} out of 5`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} aria-hidden className={star <= rating ? 'text-accent' : 'text-ink-muted opacity-40'}>
          ★
        </span>
      ))}
    </span>
  );
}

function savedLabel(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function HelpFeedbackPanel({ testId = 'help-feedback' }: { testId?: string }) {
  const session = useSession();
  const [existing, setExisting] = useState<PlatformReview | null>(null);
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  useEffect(() => {
    let alive = true;
    void api.myPlatformReview().then((result) => {
      if (!alive) return;
      setLoading(false);
      if (result.data) {
        setExisting(result.data);
        setRating(result.data.rating);
        setNote(result.data.body ?? '');
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!session.user) return null;

  const save = async () => {
    if (rating < 1) {
      setTone('danger');
      setMessage('Pick a star rating first.');
      return;
    }
    setBusy(true);
    const result = await api.savePlatformReview({ rating, body: note.trim() === '' ? null : note.trim() });
    setBusy(false);
    if (!result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That review did not save. Try again.');
      return;
    }
    setExisting(result.data);
    setRating(result.data.rating);
    setNote(result.data.body ?? '');
    setTone('success');
    setMessage(
      existing
        ? 'Review updated — the Lift2Fit team sees the latest one.'
        : 'Thank you — the Lift2Fit team reads these.',
    );
  };

  const saved = existing !== null && rating === existing.rating && note.trim() === (existing.body ?? '');

  return (
    <section
      className="flex flex-col gap-3 rounded-chat-lg border border-hairline bg-surface p-4 shadow-float"
      data-testid={testId}
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink">Rate Lift2Fit</h2>
        <p className="text-[12px] leading-relaxed text-ink-muted">
          Your review sits here, beside the thread — one per account, and you can change it any time.
        </p>
      </div>

      {loading ? (
        <p className="text-[12px] text-ink-muted">Loading your review…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1" data-testid={`${testId}-stars`}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                data-testid={`${testId}-star-${star}`}
                aria-pressed={rating === star}
                aria-label={`${star} out of 5`}
                onClick={() => setRating(star)}
                className={cn(
                  'inline-flex h-10 w-10 items-center justify-center rounded-full border text-[17px] transition-all duration-150',
                  star <= rating
                    ? 'border-accent bg-page-subtle text-accent shadow-float'
                    : 'border-hairline bg-surface text-ink-muted hover:-translate-y-0.5 hover:border-accent',
                )}
              >
                ★
              </button>
            ))}
            <span className="ml-1 text-[12px] font-bold text-ink-muted">
              {rating === 0 ? 'Not rated' : `${rating} / 5`}
            </span>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
              What should we fix or keep? (optional)
            </span>
            <textarea
              data-testid={`${testId}-note`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="One thing that works, one thing that doesn't — in your own words."
              className="l2f-chat-well w-full resize-none rounded-chat px-3 py-2 text-[13px] text-ink outline-none placeholder:text-ink-muted focus:ring-1 focus:ring-accent"
            />
          </label>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] leading-snug text-ink-muted" data-testid={`${testId}-status`}>
              {existing
                ? `You rated Lift2Fit ${existing.rating}/5 on ${savedLabel(existing.updatedAt)}.`
                : 'You have not rated Lift2Fit yet.'}
            </span>
            <Button
              size="sm"
              className="rounded-pill"
              disabled={busy || rating === 0 || saved}
              onClick={() => void save()}
              data-testid={`${testId}-save`}
            >
              {busy ? 'Saving…' : existing ? 'Update review' : 'Send review'}
            </Button>
          </div>
          <LiveMessage message={message} tone={tone} />
          <p className="text-[11px] text-ink-muted">
            Reviews are read by the Lift2Fit team only — they are never shown to a coach or a gym.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * The platform side of the same review: read-only, on the admin's thread. It is
 * the account's own words, so it is shown exactly as written.
 */
export function HelpReviewSummary({
  review,
  displayName,
  testId = 'admin-thread-review',
}: {
  review: PlatformReview | null | undefined;
  displayName: string;
  testId?: string;
}) {
  return (
    <section
      className="flex flex-col gap-2 rounded-chat-lg border border-hairline bg-surface p-4 shadow-float"
      data-testid={testId}
    >
      <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink">Their review of Lift2Fit</h2>
      {review ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StarRow rating={review.rating} size="md" />
            <span className="text-[12px] font-bold text-ink">{review.rating} / 5</span>
            <span className="text-[11px] text-ink-muted">updated {savedLabel(review.updatedAt)}</span>
          </div>
          {review.body ? (
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink">{review.body}</p>
          ) : (
            <p className="text-[12px] text-ink-muted">Stars only — no note.</p>
          )}
        </>
      ) : (
        <p className="text-[12px] text-ink-muted" data-testid={`${testId}-empty`}>
          {displayName} has not rated Lift2Fit yet.
        </p>
      )}
    </section>
  );
}
